//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useContext, useEffect } from 'react';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectCurrentCycle, selectGraphsEditable, selectGraphsReadOnly } from '@/redux/selectors/dashboardSelectors';
import { getDailyReportData, getDailyReportDataNew, getDashboardData, getGeneratedReportData } from '@/redux/slices/dashboardSlice';
import styled from '@emotion/styled';
import { Container, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { Breadcrumbs, Title, TitleBar } from '../_common';
import Refresh from '../_common/Refresh';
import CustomizeView from './CustomizeView/CustomizeView';
import DashboardDateDropdown from './DashboardDateDropdown';
import { GraphListItem } from './DefaultGraphs';
import { initializeChartjs } from './Widgets/Reusable/initializeChartjs';
import WidgetFrame from './Widgets/WidgetFrame';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import DashboardSkeleton from './DashboardSkeleton';
import { useConfigValue } from '../_hooks/useConfigValue';
import { selectAppLoading, selectAuthLoading } from '@/redux/selectors/authSelectors';
import { selectConstantsInitFinished } from '@/redux/selectors/constantsSelectors';

// initialize ChartJS so all charts are ready to go
initializeChartjs();

export type CheckedGraph = GraphListItem & { checked: boolean };

export type Graphs = {
  editable: CheckedGraph[];
  ['read-only']: CheckedGraph[];
};
const Dashboard = () => {
  const dispatch = useAppDispatch();
  const asyncReports = useContext(ConfigContext)?.dashboardConfig.graphSettings.reports?.asyncReports || false;
  const cycleDate = useAppSelector(selectCurrentCycle);
  const readOnly = useAppSelector(selectGraphsReadOnly);
  const editable = useAppSelector(selectGraphsEditable);
  const authLoading = useAppSelector(selectAuthLoading);
  const appLoading = useAppSelector(selectAppLoading);
  const constantsInitFinished = useAppSelector(selectConstantsInitFinished);
  const hasJobStagesAndPreferences = !appLoading && constantsInitFinished;
  const loading = readOnly.order.length <= 0 && editable.order.length <= 0;
  const customizeDashboard = useConfigValue(['dashboardConfig', 'customizeDashboard']);

  useEffect(() => {
    if (hasJobStagesAndPreferences) {
      dispatch(getDashboardData());
    }
  }, [cycleDate, hasJobStagesAndPreferences]);

  const onRefresh = () => {
    dispatch(getDashboardData());
    if (asyncReports) {
      dispatch(getDailyReportDataNew({ pageSize: 10, page: 0 }));
      dispatch(getGeneratedReportData({ pageSize: 10, page: 0 }));
    } else {
      dispatch(
        getDailyReportData({
          pageSize: 10,
          page: 0
        })
      );
    }
  };

  return (
    <Wrapper>
      <TitleBar>
        <Grid container alignItems="center">
          <StyledBreadcrumbs aria-label="breadcrumb">
            <Title>Dashboard</Title>
            <DashboardDateDropdown />
          </StyledBreadcrumbs>
          <DateTypography variant="h6">{format(new Date(), 'EEEE, MMMM do')}</DateTypography>
          <Refresh disabled={authLoading || !hasJobStagesAndPreferences} tooltipText="Refresh Data" onClick={onRefresh} />
        </Grid>
      </TitleBar>
      {customizeDashboard && <CustomizeView />}
      <Container sx={{ flex: '1 0 auto' }}>{loading ? <DashboardSkeleton /> : <WidgetFrame />}</Container>
    </Wrapper>
  );
};

export default Dashboard;

const DateTypography = styled(Typography)`
  font-size: 1rem;
  opacity: 0.66;
  margin: ${({ theme }) => theme.spacing(-1, 2)};
  margin-left: auto;
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  & .MuiBreadcrumbs-li .MuiButton-root {
    padding: 0px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
